import { select, take, takeEvery, put, call, fork } from 'redux-saga/effects'
import { userSelector } from 'auth/selectors'
import { sendGTAGEvent } from 'analytics/utils'
import { refreshAuthenticatedUser } from 'auth/actions'
import { userAccountIdSelector } from 'auth/selectors'
import { customerFlowType } from 'grs/helpers'
import { currencyFromSymbol } from 'shared/currency-utilities'
import featureToggles from 'shared/featureToggles'
import { searchModeSelector } from 'map/selectors'
import { indicateProgress } from '../api'
import { apiLanguageCodeSelector } from '../i18n/selectors'
import { initPayment } from '../credit-cards/actions'
import { LOCATION_CHANGE } from '../browser-location'
import {
    locationDetailsSelector,
    selectedCreditCardSelector,
    selectedUpSellOptionSelector,
    selectedVehicleDetailsSelector,
} from '../booking/selectors'
import { guestUserCardVerifiedSelector } from 'booking/selectors'
import { getReservationConfirmation } from '../confirmation/actions'
import { GET_RESERVATION_CONFIRMATION_RESPONSE } from '../confirmation/constants'
import { DISCARD_3DS, PIX_PAYMENT_METHOD, STATUS_3DS_CLOSED, STATUS_3DS_FAILURE } from 'booking/constants'
import { setQRCodeData, verify3ds } from 'booking/actions'
import { CreateReservationError } from 'basket/errors'
import { sendGoogleReserveConversionData } from 'analytics/actions'
import * as constants from './constants'
import * as actions from './actions'
import * as selectors from './selectors'
import * as api from './api'
import { reservationByIdSelector } from 'dashboard-transactions/selectors'
function* promoCodeSaga() {
    yield takeEvery(constants.REDEEM_PROMO_CODE, function* ({ item, rawPromoCode }) {
        const apiLanguageCode = yield select(apiLanguageCodeSelector)
        const { token } = yield select(userSelector)
        try {
            const result = yield call(
                indicateProgress,
                `redeemPromoCode${item.id}`,
                api.redeemPromoCode,
                item,
                rawPromoCode,
                apiLanguageCode,
                token
            )
            yield put(actions.redeemedPromoCode(item, rawPromoCode, result))
        } catch (exception) {}
    })
}

function* purchaseVerificationSaga(
    qRCodeData,
    card,
    paymentTotal,
    locationId,
    orderId,
    reservationId,
    confirmationNumber,
    orderEmailAddress
) {
    yield put(setQRCodeData(qRCodeData))
    yield put(initPayment(card, paymentTotal, locationId, orderId))

    const currentLocation = yield select(locationDetailsSelector)

    const { type, error } = yield take([
        constants.PAYMENT_SUCCESS,
        constants.PAYMENT_CANCELLED,
        constants.PAYMENT_ERROR,
    ])

    yield put(getReservationConfirmation(reservationId, confirmationNumber, orderEmailAddress))
    if (type === constants.PAYMENT_SUCCESS) {
        const basket = yield select(selectors.basketSelector)
        yield take(GET_RESERVATION_CONFIRMATION_RESPONSE)
        yield put(refreshAuthenticatedUser())
        yield put(actions.purchaseCompleted(basket, paymentTotal, orderEmailAddress))
        if (featureToggles.caPurchaseFlow) {
            yield put(sendGoogleReserveConversionData(currentLocation.grsLocation))
        }
        yield take(LOCATION_CHANGE)
        yield put(actions.clearBasket())
    } else if (type === constants.PAYMENT_ERROR) throw new Error(error)
}

function* purchaseSaga() {
    while (true) {
        const {
            singleItem,
            recaptchaToken,
            inboundNumber,
            outboundNumber,
            additionalServices,
            guestUserPersonalData,
            amendReservationId,
        } = yield take(constants.MAKE_PURCHASE)

        let guestUserReservations
        const card = yield select(selectedCreditCardSelector)
        const selectedUpSellOption = yield select(selectedUpSellOptionSelector)
        const isPixPaymentMethod = card?.id === PIX_PAYMENT_METHOD
        const userId = yield select(userAccountIdSelector)

        if (amendReservationId) {
            guestUserReservations = yield select(reservationByIdSelector(amendReservationId))
        }
        const guestUserVerifiedData = yield select(guestUserCardVerifiedSelector)
        const selectedVehicleDetails = yield select(selectedVehicleDetailsSelector)
        const apiLanguageCode = yield select(apiLanguageCodeSelector)

        try {
            yield call(indicateProgress, { names: ['makePurchase'], clearPending: true }, function* () {
                const user = yield select(userSelector)

                let items
                let paymentTotal
                let locationId
                let boletoData

                if (singleItem) {
                    items = [singleItem]
                    paymentTotal = items[0].promoCode ? items[0].promoCode.discountTotal : items[0].rate.amount
                    locationId = items[0].rate.eDataLocationId
                    boletoData = singleItem.boletoData
                } else {
                    const basket = yield select(selectors.basketSelector)
                    paymentTotal = yield select(selectors.totalSelector)
                    locationId = yield select(selectors.locationIdSelector)
                    items = basket.items
                }

                const payload = {
                    token: user?.token,
                    items: items,
                    userDetails: user?.details || guestUserPersonalData?.userPersonalData,
                    selectedVehicleDetails: selectedVehicleDetails || guestUserPersonalData?.vehicle,
                    apiLanguageCode: apiLanguageCode,
                    card: isPixPaymentMethod || window.PAYMENT_SERVICE_PROVIDER === 'INGENICO' ? undefined : card,
                    boletoData: boletoData,
                    recaptchaToken: recaptchaToken,
                    startedWithProductId: selectedUpSellOption?.startedWithProductId,
                    inboundNumber: inboundNumber,
                    outboundNumber: outboundNumber,
                    additionalServices: additionalServices,
                    PIX: isPixPaymentMethod && card.id,
                    reservationId: guestUserReservations?.reservationId,
                }

                const guestUserPayload = {
                    token: guestUserVerifiedData?.token,
                    items: items[0],
                    apiLanguageCode: apiLanguageCode,
                    userDetails: guestUserPersonalData?.userPersonalData,
                    selectedVehicleDetails: guestUserPersonalData?.vehicle,
                    card: isPixPaymentMethod || window.PAYMENT_SERVICE_PROVIDER === 'INGENICO' ? undefined : card,
                    recaptchaToken: recaptchaToken,
                    inboundNumber: inboundNumber,
                    outboundNumber: outboundNumber,
                    PIX: isPixPaymentMethod && card?.id,
                }

                if (userId && amendReservationId) {
                    //this data exists only when user comes to booking flow from manage-my-booking flow, in other case we don't have guestUserReservations.reservationId , what means user is in usual booking or purchase flow
                    const response = yield call(api.editReservation, payload)

                    const { reservationId, confirmationNumber, orderEmailAddress } = response

                    yield call(
                        purchaseVerificationSaga,
                        null,
                        card,
                        paymentTotal,
                        locationId,
                        null,
                        reservationId,
                        confirmationNumber,
                        orderEmailAddress
                    )
                } else {
                    let response
                    if (!user && !amendReservationId && guestUserPersonalData) {
                        response = yield call(api.createGuestUserReservation, guestUserPayload)
                    }
                    if (!user && amendReservationId) {
                        response = yield call(api.editGuestUserReservation, payload)
                    }
                    if (user && !amendReservationId) {
                        response = yield call(api.createReservation, payload)
                    }

                    const {
                        orderId,
                        reservationId,
                        qRCodeData,
                        challengeURL3DS,
                        challengeData3DS,
                        confirmationNumber,
                        orderEmailAddress,
                    } = response

                    sendGTAGEvent(
                        'purchase',
                        {
                            transaction_id: reservationId,
                            currency: currencyFromSymbol(items[0]?.rate?.currencySymbol),
                            value: paymentTotal,
                            discount: items[0]?.promoCode?.promoDiscount || null,
                            coupon: items[0]?.promoCode?.barcode || null,
                            location_id: locationId,
                        },
                        userId
                    )

                    if (challengeURL3DS) {
                        yield put(verify3ds(challengeURL3DS, challengeData3DS))

                        const { payload } = yield take(DISCARD_3DS)

                        // User cancelled(in 3ds) or 3ds verification failed
                        if (payload.type === STATUS_3DS_FAILURE) {
                            throw new CreateReservationError(payload?.errorMsg)
                        } else if (payload.type === STATUS_3DS_CLOSED) {
                            // User closed 3ds modal
                            return
                        }

                        yield call(
                            purchaseVerificationSaga,
                            qRCodeData,
                            card,
                            paymentTotal,
                            locationId,
                            orderId,
                            reservationId
                        )
                    } else {
                        yield call(
                            purchaseVerificationSaga,
                            qRCodeData,
                            card,
                            paymentTotal,
                            locationId,
                            orderId,
                            reservationId,
                            confirmationNumber,
                            orderEmailAddress
                        )
                    }
                }
            })
        } catch (exception) {
            yield put(actions.purchaseError())
        }
    }
}

export default function* rootSaga() {
    yield fork(promoCodeSaga)
    yield fork(purchaseSaga)
}
